import React, { useState } from 'react';
import {
  Table as MUITable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import TableContextMenu from '../../../../common/components/Table/ContextMenu';
import { ContextMenuAction } from '../../../../common/components/Table/types';
import { RemunerationGroupRow } from '../hooks';

interface RemunerationTableProps {
  data: RemunerationGroupRow[];
  onContextMenuAction?: (action: ContextMenuAction, row: RemunerationGroupRow) => void;
  rowBackgroundColor?: string;
}

export function RemunerationTable({
  data,
  onContextMenuAction,
  rowBackgroundColor,
}: RemunerationTableProps) {
  // Determine background color based on data direction if not explicitly provided
  const getRowBackgroundColor = (row: any) => {
    if (rowBackgroundColor) return rowBackgroundColor;
    
    const direction = (row as any).direction;
    if (direction === 'INBOUND') return 'rgb(230, 245, 230)';
    if (direction === 'OUTBOUND') return 'rgb(255, 240, 240)';
    
    return undefined; // Default styling
  };
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    row: RemunerationGroupRow | null;
  } | null>(null);

  const handleRowContextMenu = (row: RemunerationGroupRow, event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      row: row,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleContextMenuAction = (action: ContextMenuAction) => {
    if (!contextMenu?.row) return;

    if (onContextMenuAction) {
      onContextMenuAction(action, contextMenu.row);
    }
    
    handleCloseContextMenu();
  };

  return (
    <Box sx={{ width: '100%' }} data-testid="remuneration-table-wrapper">

      {/* Table Container */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #e8f4fd',
          backgroundColor: '#ffffff',
        }}
        data-testid="remuneration-table-container"
      >
        <TableContainer
          sx={{
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#a8a8a8',
              },
            },
          }}
        >
          <MUITable
            sx={{
              minWidth: '100%',
              backgroundColor: '#ffffff',
            }}
            data-testid="remuneration-data-table"
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#f8fafc',
                  '& th': {
                    borderBottom: '2px solid #e2e8f0',
                    padding: isMobile ? '8px 6px' : '12px 10px',
                    backgroundColor: '#f8fafc',
                  },
                }}
                data-testid="remuneration-table-header-row"
              >
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '100px' : '120px',
                    borderRight: '1px solid #e2e8f0',
                  }}
                >
                  Sending DO
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '100px' : '120px',
                    borderRight: '1px solid #e2e8f0',
                  }}
                >
                  Receiving DO
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '120px' : '150px',
                    borderRight: '1px solid #e2e8f0',
                  }}
                >
                  Name Of Group
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '60px' : '80px',
                    borderRight: '1px solid #e2e8f0',
                  }}
                >
                  Year
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '60px' : '80px',
                    borderRight: '1px solid #e2e8f0',
                  }}
                >
                  Quarter
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '100px' : '120px',
                    borderRight: '1px solid #e2e8f0',
                  }}
                >
                  OE From Code
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '120px' : '140px',
                    borderRight: '1px solid #e2e8f0',
                  }}
                >
                  Not Subj. To Sampling
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '80px' : '100px',
                    borderRight: '1px solid #e2e8f0',
                  }}
                >
                  Comments
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '120px' : '150px',
                    borderRight: '1px solid #e2e8f0',
                  }}
                >
                  Latest Activity / DO
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: isMobile ? '12px' : '14px',
                    textAlign: 'center',
                    minWidth: isMobile ? '60px' : '80px',
                    backgroundColor: '#f1f5f9',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow
                  key={`${row.sendingPpo?.name}-${row.receivingPpo?.name}-${row.year}-${row.quarter}-${index}`}
                  sx={{
                    backgroundColor: getRowBackgroundColor(row) || (index % 2 === 0 ? '#ffffff' : '#f8fafc'),
                    '&:hover': {
                      backgroundColor: (() => {
                        const bgColor = getRowBackgroundColor(row);
                        if (bgColor === 'rgb(230, 245, 230)') return '#c8e6c9';
                        if (bgColor === 'rgb(255, 240, 240)') return '#ffcdd2';
                        return '#f1f5f9';
                      })(),
                      transition: 'background-color 0.2s ease',
                    },
                    '& td': {
                      borderBottom: '1px solid #e2e8f0',
                      padding: isMobile ? '8px 6px' : '2px 10px',
                      borderRight: '1px solid #e2e8f0',
                    },
                    '& td:last-child': {
                      borderRight: 'none',
                      backgroundColor: getRowBackgroundColor(row) || '#f1f5f9',
                    },
                  }}
                  onContextMenu={(e) => handleRowContextMenu(row, e)}
                  data-testid={`remuneration-table-row-${index}`}
                >
                  <TableCell
                    sx={{
                      color: '#334155',
                      textAlign: 'center',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 500,
                    }}
                    data-testid={`sending-ppo-cell-${index}`}
                  >
                    {row.sendingPpo?.name || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#334155',
                      textAlign: 'center',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 500,
                    }}
                    data-testid={`receiving-ppo-cell-${index}`}
                  >
                    {row.receivingPpo?.name || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#334155',
                      textAlign: 'center',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 500,
                    }}
                    data-testid={`group-name-cell-${index}`}
                  >
                    {row.remunerationGroup?.displayName || row.remunerationGroup?.name || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#334155',
                      textAlign: 'center',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 500,
                    }}
                    data-testid={`year-cell-${index}`}
                  >
                    {row.year || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#334155',
                      textAlign: 'center',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 500,
                    }}
                    data-testid={`quarter-cell-${index}`}
                  >
                    {row.quarter ? `${row.quarter}` : '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#334155',
                      textAlign: 'center',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 500,
                    }}
                    data-testid={`oe-code-cell-${index}`}
                  >
                    {(() => {
                      // Use exchangeOfficesAsString if available, otherwise fall back to impcEntities
                      const exchangeOfficesAsString = (row as any).exchangeOfficesAsString;
                      if (exchangeOfficesAsString) {
                        return exchangeOfficesAsString;
                      }
                      
                      // Fallback to impcEntities if exchangeOfficesAsString is not available
                      const impcEntities = (row as any).impcEntities;
                      if (impcEntities && Array.isArray(impcEntities) && impcEntities.length > 0) {
                        return impcEntities.map((entity: any) => entity?.name || entity.name || entity).join(', ');
                      }
                      
                      // If neither is available, show "all other"
                      return 'all other';
                    })()}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#334155',
                      textAlign: 'center',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 500,
                    }}
                    data-testid={`sampling-cell-${index}`}
                  >
                    {row.notSubjToSampling ? 'Y' : ''}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#334155',
                      textAlign: 'center',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 500,
                    }}
                    data-testid={`comments-cell-${index}`}
                  >
                    {row.hasComments ? 'Y' : ''}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#334155',
                      textAlign: 'center',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 500,
                    }}
                    data-testid={`latest-activity-cell-${index}`}
                  >
                    {(() => {
                      const latestActivityTime = (row as any).latestActivityTime;
                      const latestActivityPpo = row.latestActivityPpo?.name;
                      
                      if (latestActivityTime && latestActivityPpo) {
                        // Format the date as "12-Sep-2025 10:26 DKA"
                        const date = new Date(latestActivityTime);
                        const day = date.getDate();
                        const month = date.toLocaleDateString('en-US', { month: 'short' });
                        const year = date.getFullYear();
                        const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                        return `${day}-${month}-${year} ${time} ${latestActivityPpo}`;
                      } else if (latestActivityPpo) {
                        return `- ${latestActivityPpo}`;
                      } else if (latestActivityTime) {
                        const date = new Date(latestActivityTime);
                        const day = date.getDate();
                        const month = date.toLocaleDateString('en-US', { month: 'short' });
                        const year = date.getFullYear();
                        const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                        return `${day}-${month}-${year} ${time}`;
                      }
                      return '-';
                    })()}
                  </TableCell>

                  {/* Actions Cell */}
                  <TableCell
                    sx={{
                      textAlign: 'center',
                      backgroundColor: '#f1f5f9',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowContextMenu(row, e as React.MouseEvent);
                      }}
                      sx={{
                        padding: '6px',
                        color: '#64748b',
                        '&:hover': {
                          backgroundColor: '#e2e8f0',
                          color: '#475569',
                        },
                        transition: 'all 0.2s ease',
                      }}
                      data-testid={`more-actions-button-${index}`}
                    >
                      <MoreVertIcon
                        sx={{
                          fontSize: isMobile ? 16 : 18,
                        }}
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MUITable>
        </TableContainer>
      </Paper>

      {/* Context Menu */}
      {contextMenu !== null && (
        <TableContextMenu
          open={true}
          onClose={handleCloseContextMenu}
          onAction={handleContextMenuAction}
          anchorPosition={{
            top: contextMenu.mouseY,
            left: contextMenu.mouseX,
          }}
          currentRow={contextMenu.row}
          editModeOnly={false}
          viewMode={false}
          customActions={[
            {
              label: 'Edit Records/Groups',
              action: 'editSamples',
              icon: '✏️',
            },
            {
              label: 'Comment',
              action: 'comment',
              icon: '💬',
            },
          ]}
          hideDefaultActions={true}
          data-testid="remuneration-table-context-menu"
        />
      )}
    </Box>
  );
}
